import React, { useRef } from "react";
import Footer from "./Footer";
import { ChevronRight, CircleCheck, XCircle } from "lucide-react";
import useStickyButton from "../hooks/useStickyButton";

const Guideline = (props) => {
  const { positiveSampleImage, negativeSampleImage, myTenteraSide, onNext, uiConfig = {} } = props;

  const inputRef = useRef(null);
  const { buttonContainerRef, sentinelRef, isSticky, buttonHeight } = useStickyButton();

  // Safe access
  const global = uiConfig?.Global || {};
  const textColor = global?.text?.fontColor || "#6b7280"; // default gray-500
  const highlightTextColor = global?.highlightText?.fontColor || "#2563eb"; // blue-600
  const buttonBgColor = global?.button?.backgroundColor || "#2563eb"; // blue-600
  const buttonTextColor = global?.button?.fontColor || "#ffffff"; // white
  const pageBgColor = global?.pageContainer?.backgroundColor || "#ffffff"; // white

  const onChange = async (e) => {
    const file = e.target.files[0];
    const reader = new FileReader();

    reader.readAsArrayBuffer(file);

    reader.onload = async () => {
      onNext(reader.result);
    };
  };

  const handleOpenCamera = () => {
    inputRef.current?.click();
  };

  return (
    <div
      style={{
        background: pageBgColor,
        padding: "1rem",
        // borderTopLeftRadius: "0.75rem",
        // borderTopRightRadius: "0.75rem",
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        height: "100%",
      }}
    >
      <h2
        style={{
          fontSize: "1.25rem",
          fontWeight: "bold",
          marginBottom: "0.5rem",
          textAlign: "center",
          color: textColor,
        }}
      >
        Please take a clear photo of your
        <br />
        <span style={{ color: highlightTextColor }}>MyTentera ({myTenteraSide === "front" ? "front" : "back"})</span>
      </h2>

      <p
        style={{
          marginBottom: "1.5rem",
          textAlign: "left",
          maxWidth: "65ch",
          color: textColor,
        }}
      >
        Position your MyTentera correctly, ensure good lighting, and avoid glare or reflections. See our example below for
        guidance. You can place the MyTentera on your palm to angle it if required.
      </p>

      {/* Positive Image */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", position: "relative" }}>
        <div style={{ marginBottom: "0.5rem", display: "flex", justifyContent: "center" }}>
          <img
            src={positiveSampleImage}
            style={{
              height: "auto",
              width: "20rem",
              maxHeight: "200px",
              objectFit: "contain",
              borderRadius: "0.5rem",
              boxShadow: "0 2px 8px rgba(0,0,0,0.5)",
            }}
          />
        </div>
        <CircleCheck
          style={{
            color: "#16a34a", // green-600
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            opacity: 0.5,
          }}
          width={80}
          height={80}
        />
      </div>
      <p
        style={{
          fontSize: "0.875rem",
          textAlign: "center",
          fontWeight: "bold",
          marginTop: "0.25rem",
          color: textColor,
        }}
      >
        Good: No glare & reflection. Correct angle.
      </p>

      {/* Spacer */}
      <div style={{ marginTop: "1rem" }} />

      {/* Negative Image */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", position: "relative" }}>
        <div style={{ marginBottom: "0.5rem", display: "flex", justifyContent: "center" }}>
          <img
            src={negativeSampleImage}
            style={{
              height: "auto",
              width: "20rem",
              maxHeight: "200px",
              objectFit: "contain",
              borderRadius: "0.5rem",
              boxShadow: "0 2px 8px rgba(0,0,0,0.5)",
            }}
          />
        </div>
        <XCircle
          style={{
            color: "#dc2626", // red-600
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            opacity: 0.5,
          }}
          width={80}
          height={80}
        />
      </div>
      <p
        style={{
          fontSize: "0.875rem",
          textAlign: "center",
          fontWeight: "bold",
          marginTop: "0.25rem",
          color: textColor,
        }}
      >
        Bad: Glare & reflection.
      </p>

      <div style={{ marginBottom: "2rem", flex: 1 }} />

      {/* Sentinel element for Intersection Observer */}
      <div ref={sentinelRef} style={{ height: "1px", visibility: "hidden" }} />

      {/* Spacer to maintain space when button is sticky */}
      {isSticky && buttonHeight > 0 && (
        <div style={{ height: `${buttonHeight}px`, width: "100%" }} />
      )}

      {/* Button */}
      <div 
        ref={buttonContainerRef}
        style={{
          position: isSticky ? "fixed" : "relative",
          bottom: isSticky ? "0" : "auto",
          left: isSticky ? "0" : "auto",
          right: isSticky ? "0" : "auto",
          padding: isSticky ? "1rem" : "0",
          background: "transparent",
          zIndex: isSticky ? "50" : "auto",
          transition: "all 0.3s ease-in-out",
          pointerEvents: isSticky ? "none" : "auto",
        }}
      >
        <button
          style={{
            width: "66%",
            padding: "0.75rem 1rem",
            borderRadius: "9999px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
            margin: "0 auto",
            background: buttonBgColor,
            color: buttonTextColor,
            transition: "transform 0.15s ease-in-out",
            pointerEvents: "auto",
          }}
          onClick={handleOpenCamera}
          onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
          onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          onTouchStart={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
          onTouchEnd={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          <span>Continue</span>
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      <input ref={inputRef} accept="image/*" type="file" capture="environment" hidden onChange={onChange} />

      <Footer
        style={{
          fontSize: "0.75rem",
          color: textColor,
          marginTop: "1rem",
        }}
      />
    </div>
  );
};

export default Guideline;
